import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { 
  Book, 
  BookOpen, 
  Calendar, 
  CheckCircle, 
  Clock, 
  Filter, 
  Loader, 
  Pencil, 
  Search, 
  User, 
  Users, 
  XCircle,
  MessageSquare,
  AlertTriangle,
  X,
  School,
  ArrowLeft,
  Building
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';

/**
 * Admin Requests Page
 * Shows all schools with pending requests, then allows drilling down to specific school requests
 */
const AdminRequestsPage = () => {
  const { user } = useContext(AuthContext);
  const [currentView, setCurrentView] = useState('schools'); // 'schools' or 'requests'
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [activeTab, setActiveTab] = useState('course');
  const [schools, setSchools] = useState([]);
  const [courseRequests, setCourseRequests] = useState([]);
  const [sectionRequests, setSectionRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modals state
  const [viewRequest, setViewRequest] = useState(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [modalNotes, setModalNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch schools with request counts
  useEffect(() => {
    if (currentView === 'schools') {
      fetchSchoolsWithRequestCounts();
    }
  }, [currentView]);

  // Fetch requests data when viewing specific school
  useEffect(() => {
    if (currentView === 'requests' && selectedSchool) {
      fetchRequestsForSchool(selectedSchool.id);
    }
  }, [currentView, selectedSchool, statusFilter]);

  const fetchSchoolsWithRequestCounts = async () => {
    try {
      setLoading(true);
      
      // Fetch all schools
      const schoolsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/schools`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!schoolsResponse.ok) {
        throw new Error('Failed to fetch schools');
      }
      
      const schoolsData = await schoolsResponse.json();
      
      // Fetch request counts for each school
      const schoolsWithCounts = await Promise.all(
        schoolsData.map(async (school) => {
          try {
            // Get course request count
            const courseResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/api/admin/course-requests/count?schoolId=${school.id}&status=pending`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            // Get section request count
            const sectionResponse = await fetch(
              `${process.env.REACT_APP_API_URL}/api/admin/section-requests/count?schoolId=${school.id}&status=pending`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              }
            );
            
            let courseCount = 0;
            let sectionCount = 0;
            
            if (courseResponse.ok) {
              const courseData = await courseResponse.json();
              courseCount = courseData.count || 0;
            }
            
            if (sectionResponse.ok) {
              const sectionData = await sectionResponse.json();
              sectionCount = sectionData.count || 0;
            }
            
            return {
              ...school,
              pendingCourseRequests: courseCount,
              pendingSectionRequests: sectionCount,
              totalPendingRequests: courseCount + sectionCount
            };
          } catch (error) {
            console.error(`Error fetching request counts for school ${school.id}:`, error);
            return {
              ...school,
              pendingCourseRequests: 0,
              pendingSectionRequests: 0,
              totalPendingRequests: 0
            };
          }
        })
      );
      
      // Sort schools by total pending requests (highest first)
      schoolsWithCounts.sort((a, b) => b.totalPendingRequests - a.totalPendingRequests);
      
      setSchools(schoolsWithCounts);
    } catch (error) {
      console.error('Error fetching schools with request counts:', error);
      setError(error.message || 'An error occurred while loading schools');
      toast.error('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestsForSchool = async (schoolId) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('schoolId', schoolId);
      if (statusFilter && statusFilter !== '') {
        queryParams.append('status', statusFilter);
      }
      
      // Fetch course requests for this school
      const courseResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/course-requests?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!courseResponse.ok) {
        throw new Error('Failed to fetch course requests');
      }
      
      const courseData = await courseResponse.json();
      setCourseRequests(courseData.requests || []);
      
      // Fetch section requests for this school
      const sectionResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/section-requests?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!sectionResponse.ok) {
        throw new Error('Failed to fetch section requests');
      }
      
      const sectionData = await sectionResponse.json();
      setSectionRequests(sectionData.requests || []);
    } catch (error) {
      console.error('Error fetching requests for school:', error);
      setError(error.message || 'An error occurred while loading requests');
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolClick = (school) => {
    setSelectedSchool(school);
    setCurrentView('requests');
    setStatusFilter('pending'); // Reset to pending when entering school view
  };

  const handleBackToSchools = () => {
    setCurrentView('schools');
    setSelectedSchool(null);
    setCourseRequests([]);
    setSectionRequests([]);
    setSearchQuery('');
    setError(null);
  };

  // Handle approving a request
  const handleApprove = async () => {
    try {
      setActionLoading(true);
      
      let endpoint;
      if (viewRequest.type === 'course') {
        endpoint = `${process.env.REACT_APP_API_URL}/api/admin/course-requests/${viewRequest.id}/approve`;
      } else {
        endpoint = `${process.env.REACT_APP_API_URL}/api/admin/section-requests/${viewRequest.id}/approve`;
      }
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: modalNotes })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to approve request');
      }
      
      // Update the request lists
      if (viewRequest.type === 'course') {
        setCourseRequests(courseRequests.map(req => 
          req.id === viewRequest.id ? { ...req, status: 'approved' } : req
        ));
      } else {
        setSectionRequests(sectionRequests.map(req => 
          req.id === viewRequest.id ? { ...req, status: 'approved' } : req
        ));
      }
      
      toast.success('Request approved successfully');
      setApproveModalOpen(false);
      setViewRequest(null);
      setModalNotes('');
      
      // Refresh school counts if we're viewing pending requests
      if (statusFilter === 'pending') {
        fetchSchoolsWithRequestCounts();
      }
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.message || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle rejecting a request
  const handleReject = async () => {
    try {
      setActionLoading(true);
      
      if (!modalNotes.trim()) {
        toast.error('Please provide a reason for rejection');
        return;
      }
      
      let endpoint;
      if (viewRequest.type === 'course') {
        endpoint = `${process.env.REACT_APP_API_URL}/api/admin/course-requests/${viewRequest.id}/reject`;
      } else {
        endpoint = `${process.env.REACT_APP_API_URL}/api/admin/section-requests/${viewRequest.id}/reject`;
      }
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: modalNotes })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reject request');
      }
      
      // Update the request lists
      if (viewRequest.type === 'course') {
        setCourseRequests(courseRequests.map(req => 
          req.id === viewRequest.id ? { ...req, status: 'rejected' } : req
        ));
      } else {
        setSectionRequests(sectionRequests.map(req => 
          req.id === viewRequest.id ? { ...req, status: 'rejected' } : req
        ));
      }
      
      toast.success('Request rejected successfully');
      setRejectModalOpen(false);
      setViewRequest(null);
      setModalNotes('');
      
      // Refresh school counts if we're viewing pending requests
      if (statusFilter === 'pending') {
        fetchSchoolsWithRequestCounts();
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.message || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  // Get course request details
  const fetchCourseRequestDetails = async (courseRequest) => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/course-requests/${courseRequest.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch course request details');
      }
      
      const data = await response.json();
      setViewRequest({
        ...data.courseRequest,
        sectionRequest: data.sectionRequest,
        type: 'course'
      });
    } catch (error) {
      console.error('Error fetching course request details:', error);
      toast.error('Failed to load course request details');
    } finally {
      setLoading(false);
    }
  };

  // Get section request details
  const fetchSectionRequestDetails = async (sectionRequest) => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/section-requests/${sectionRequest.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch section request details');
      }
      
      const data = await response.json();
      setViewRequest({
        ...data,
        type: 'section'
      });
    } catch (error) {
      console.error('Error fetching section request details:', error);
      toast.error('Failed to load section request details');
    } finally {
      setLoading(false);
    }
  };

  // Filter requests by search query
  const filteredCourseRequests = courseRequests.filter(request => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (request.code && request.code.toLowerCase().includes(searchLower)) ||
      (request.name && request.name.toLowerCase().includes(searchLower)) ||
      (request.departmentName && request.departmentName.toLowerCase().includes(searchLower)) ||
      (request.requestedByName && request.requestedByName.toLowerCase().includes(searchLower))
    );
  });
  
  const filteredSectionRequests = sectionRequests.filter(request => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (request.courseCode && request.courseCode.toLowerCase().includes(searchLower)) ||
      (request.courseName && request.courseName.toLowerCase().includes(searchLower)) ||
      (request.semester && request.semester.toLowerCase().includes(searchLower)) ||
      (request.professorName && request.professorName.toLowerCase().includes(searchLower)) ||
      (request.requestedByName && request.requestedByName.toLowerCase().includes(searchLower))
    );
  });

  // Filter schools by search query
  const filteredSchools = schools.filter(school => {
    const searchLower = searchQuery.toLowerCase();
    return school.name.toLowerCase().includes(searchLower) ||
           school.domain.toLowerCase().includes(searchLower);
  });

  // Status badge component
  const StatusBadge = ({ status }) => {
    let color;
    let icon;
    
    switch (status) {
      case 'approved':
        color = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        icon = <CheckCircle className="w-4 h-4 mr-1" />;
        break;
      case 'rejected':
        color = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        icon = <XCircle className="w-4 h-4 mr-1" />;
        break;
      case 'pending':
      default:
        color = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
        icon = <Clock className="w-4 h-4 mr-1" />;
        break;
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex items-center">
          {currentView === 'requests' && (
            <button
              onClick={handleBackToSchools}
              className="mr-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {currentView === 'schools' 
                ? 'Course and Section Requests by School' 
                : `Requests for ${selectedSchool?.name}`
              }
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {currentView === 'schools'
                ? 'Review requests organized by school'
                : 'Review and manage student requests for new courses and sections'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Schools View */}
      {currentView === 'schools' && (
        <>
          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search schools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <Loader className="h-8 w-8 text-red-500 animate-spin" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading schools...</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Schools List */}
          {!loading && !error && (
            <>
              {filteredSchools.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
                  <School className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No schools found
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {searchQuery ? 'Try adjusting your search terms.' : 'No schools are available.'}
                  </p>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          School
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Course Requests
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Section Requests
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Total Pending
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredSchools.map((school) => (
                        <tr key={school.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                                  <School className="h-6 w-6 text-red-600 dark:text-red-400" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {school.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {school.domain}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <BookOpen className="h-4 w-4 text-blue-500 mr-1" />
                              <span className="text-sm text-gray-900 dark:text-white">
                                {school.pendingCourseRequests}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-green-500 mr-1" />
                              <span className="text-sm text-gray-900 dark:text-white">
                                {school.pendingSectionRequests}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              school.totalPendingRequests > 0 
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                            }`}>
                              {school.totalPendingRequests}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleSchoolClick(school)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            >
                              View Requests
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Requests View */}
      {currentView === 'requests' && selectedSchool && (
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav className="flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('course')}
                className={`py-4 px-1 ${
                  activeTab === 'course'
                    ? 'border-red-500 text-red-600 dark:text-red-400 border-b-2 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <BookOpen className="w-5 h-5 mr-2" />
                  Course Requests
                  <span className="ml-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full text-xs">
                    {courseRequests.filter(r => statusFilter === '' || r.status === statusFilter).length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('section')}
                className={`py-4 px-1 ${
                  activeTab === 'section'
                    ? 'border-red-500 text-red-600 dark:text-red-400 border-b-2 font-medium'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Section Requests
                  <span className="ml-2 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full text-xs">
                    {sectionRequests.filter(r => statusFilter === '' || r.status === statusFilter).length}
                  </span>
                </div>
              </button>
            </nav>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={`Search ${activeTab === 'course' ? 'courses' : 'sections'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white py-2 pl-3 pr-10 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">All Requests</option>
                <option value="pending">Pending Requests</option>
                <option value="approved">Approved Requests</option>
                <option value="rejected">Rejected Requests</option>
              </select>
            </div>
          </div>

          {/* Loading state */}
          {loading && !viewRequest && (
            <div className="flex justify-center items-center py-12">
              <Loader className="h-8 w-8 text-red-500 animate-spin" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading requests...</span>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Course Requests */}
          {activeTab === 'course' && !loading && !error && (
            <>
              {filteredCourseRequests.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
                  <BookOpen className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No course requests found
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {statusFilter 
                      ? `There are no ${statusFilter} course requests for this school.`
                      : 'There are no course requests for this school.'}
                  </p>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Course Details
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Department
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Requested By
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredCourseRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                  <Book className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {request.code}: {request.name}
                                </div>
                                {request.newDepartmentCode && (
                                  <div className="text-xs text-green-600 dark:text-green-400">
                                    + New Department: {request.newDepartmentCode}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {request.departmentName ? `${request.departmentName} (${request.departmentCode})` : 'New Department'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">{request.requestedByName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <StatusBadge status={request.status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => fetchCourseRequestDetails(request)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 mr-3"
                            >
                              View
                            </button>
                            {request.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    fetchCourseRequestDetails(request);
                                    setApproveModalOpen(true);
                                  }}
                                  className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => {
                                    fetchCourseRequestDetails(request);
                                    setRejectModalOpen(true);
                                  }}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Section Requests */}
          {activeTab === 'section' && !loading && !error && (
            <>
              {filteredSectionRequests.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
                  <Calendar className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No section requests found
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {statusFilter 
                      ? `There are no ${statusFilter} section requests for this school.`
                      : 'There are no section requests for this school.'}
                  </p>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Course
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Section Details
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Professor
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredSectionRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                  <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {request.courseCode}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {request.courseName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {request.semester} {request.year}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {request.professorName || request.newProfessorName || 'Unknown'}
                              {request.newProfessorName && (
                                <span className="ml-1 text-xs text-green-600 dark:text-green-400">(New)</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <StatusBadge status={request.status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => fetchSectionRequestDetails(request)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 mr-3"
                            >
                              View
                            </button>
                            {request.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    fetchSectionRequestDetails(request);
                                    setApproveModalOpen(true);
                                  }}
                                  className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => {
                                    fetchSectionRequestDetails(request);
                                    setRejectModalOpen(true);
                                  }}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Request Details Modal - Same as moderator version */}
      {viewRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setViewRequest(null)}>
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={() => setViewRequest(null)}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Request details content - similar to moderator version */}
              {viewRequest.type === 'course' ? (
                <div>
                  <div className="flex items-center mb-4">
                    <div className="mr-4 bg-blue-100 dark:bg-blue-900 p-2 rounded-full">
                      <Book className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                        Course Request Details
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Requested on {new Date(viewRequest.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <StatusBadge status={viewRequest.status} />
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Course Code</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{viewRequest.code}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Course Name</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{viewRequest.name}</dd>
                      </div>
                      
                      {viewRequest.description && (
                        <div className="col-span-2">
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-white">{viewRequest.description}</dd>
                        </div>
                      )}
                      
                      {viewRequest.departmentId ? (
                        <div className="col-span-2">
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Department</dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                            {viewRequest.departmentName} ({viewRequest.departmentCode})
                          </dd>
                        </div>
                      ) : (
                        <div className="col-span-2">
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">New Department</dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-white flex items-center">
                            <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-1 rounded text-xs font-medium mr-2">
                              NEW
                            </span>
                            {viewRequest.newDepartmentCode}: {viewRequest.newDepartmentName}
                          </dd>
                        </div>
                      )}
                      
                      <div className="col-span-2">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Requested By</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white flex items-center">
                          <User className="h-4 w-4 mr-1 text-gray-400" />
                          {viewRequest.requestedByName}
                        </dd>
                      </div>
                      
                      {viewRequest.sectionRequest && (
                        <>
                          <div className="col-span-2">
                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
                              Includes Section Request
                            </dt>
                            <dd className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                              <div>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Term</span>
                                <p className="text-sm text-gray-900 dark:text-white">
                                  {viewRequest.sectionRequest.semester} {viewRequest.sectionRequest.year}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Professor</span>
                                <p className="text-sm text-gray-900 dark:text-white">
                                  {viewRequest.sectionRequest.professorId ? (
                                    viewRequest.sectionRequest.professorName
                                  ) : (
                                    <span className="flex items-center">
                                      <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-0.5 rounded text-xs font-medium mr-1">
                                        NEW
                                      </span>
                                      {viewRequest.sectionRequest.newProfessorName}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</span>
                                <p className="text-sm text-gray-900 dark:text-white">
                                  <StatusBadge status={viewRequest.sectionRequest.status} />
                                </p>
                              </div>
                            </dd>
                          </div>
                        </>
                      )}
                      
                      {viewRequest.status !== 'pending' && (
                        <>
                          <div className="col-span-2">
                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Resolved By</dt>
                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                              {viewRequest.moderatorName || viewRequest.adminName} on {new Date(viewRequest.resolvedAt).toLocaleDateString()}
                            </dd>
                          </div>
                          
                          {(viewRequest.moderatorNotes || viewRequest.adminNotes) && (
                            <div className="col-span-2">
                              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Notes</dt>
                              <dd className="mt-1 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                {viewRequest.moderatorNotes || viewRequest.adminNotes}
                              </dd>
                            </div>
                          )}
                        </>
                      )}
                    </dl>
                  </div>
                  
                  {viewRequest.status === 'pending' && (
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                      <button
                        type="button"
                        onClick={() => setApproveModalOpen(true)}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm"
                      >
                        <CheckCircle className="mr-2 h-5 w-5" />
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => setRejectModalOpen(true)}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                      >
                        <XCircle className="mr-2 h-5 w-5 text-red-500" />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center mb-4">
                    <div className="mr-4 bg-blue-100 dark:bg-blue-900 p-2 rounded-full">
                      <Calendar className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                        Section Request Details
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Requested on {new Date(viewRequest.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="ml-auto">
                      <StatusBadge status={viewRequest.status} />
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                      <div className="col-span-2">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Course</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {viewRequest.courseCode}: {viewRequest.courseName}
                        </dd>
                      </div>
                      
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Term</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {viewRequest.semester} {viewRequest.year}
                        </dd>
                      </div>
                      
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Professor</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                          {viewRequest.professorId ? (
                            viewRequest.professorName
                          ) : (
                            <span className="flex items-center">
                              <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-0.5 rounded text-xs font-medium mr-1">
                                NEW
                              </span>
                              {viewRequest.newProfessorName}
                            </span>
                          )}
                        </dd>
                      </div>
                      
                      <div className="col-span-2">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Requested By</dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-white flex items-center">
                          <User className="h-4 w-4 mr-1 text-gray-400" />
                          {viewRequest.requestedByName}
                        </dd>
                      </div>
                      
                      {viewRequest.status !== 'pending' && (
                        <>
                          <div className="col-span-2">
                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Resolved By</dt>
                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                              {viewRequest.moderatorName || viewRequest.adminName} on {new Date(viewRequest.resolvedAt).toLocaleDateString()}
                            </dd>
                          </div>
                          
                          {(viewRequest.moderatorNotes || viewRequest.adminNotes) && (
                            <div className="col-span-2">
                              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Notes</dt>
                              <dd className="mt-1 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                                {viewRequest.moderatorNotes || viewRequest.adminNotes}
                              </dd>
                            </div>
                          )}
                        </>
                      )}
                    </dl>
                  </div>
                  
                  {viewRequest.status === 'pending' && (
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                      <button
                        type="button"
                        onClick={() => setApproveModalOpen(true)}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm"
                      >
                        <CheckCircle className="mr-2 h-5 w-5" />
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => setRejectModalOpen(true)}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                      >
                        <XCircle className="mr-2 h-5 w-5 text-red-500" />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal - Same as moderator version */}
      {approveModalOpen && viewRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => !actionLoading && setApproveModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Approve {viewRequest.type === 'course' ? 'Course' : 'Section'} Request
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {viewRequest.type === 'course' 
                        ? `This will create a new course ${viewRequest.code}: ${viewRequest.name}.` 
                        : `This will create a new section for ${viewRequest.courseCode} (${viewRequest.semester} ${viewRequest.year}).`
                      }
                      {viewRequest.type === 'course' && !viewRequest.departmentId && ' A new department will also be created.'}
                      {(viewRequest.type === 'course' && viewRequest.sectionRequest) || (viewRequest.type === 'section' && !viewRequest.professorId) 
                        ? ' A new professor will also be created.' 
                        : ''
                      }
                    </p>
                  </div>
                  
                  <div className="mt-4">
                    <label htmlFor="approve-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
                      Notes (Optional)
                    </label>
                    <textarea
                      id="approve-notes"
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="Add any notes about this approval..."
                      value={modalNotes}
                      onChange={(e) => setModalNotes(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={handleApprove}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm ${
                    actionLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {actionLoading ? (
                    <>
                      <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Processing...
                    </>
                  ) : 'Approve'}
                </button>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => setApproveModalOpen(false)}
                  className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:col-start-1 sm:text-sm ${
                    actionLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal - Same as moderator version */}
      {rejectModalOpen && viewRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => !actionLoading && setRejectModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                  <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Reject {viewRequest.type === 'course' ? 'Course' : 'Section'} Request
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Please provide a reason for rejecting this request. This feedback will be visible to the student who made the request.
                    </p>
                  </div>
                  
                  <div className="mt-4">
                    <label htmlFor="reject-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-left">
                      Reason for Rejection <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="reject-notes"
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      placeholder="Explain why this request is being rejected..."
                      value={modalNotes}
                      onChange={(e) => setModalNotes(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  disabled={actionLoading || !modalNotes.trim()}
                  onClick={handleReject}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm ${
                    actionLoading || !modalNotes.trim() ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {actionLoading ? (
                    <>
                      <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Processing...
                    </>
                  ) : 'Reject'}
                </button>
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => setRejectModalOpen(false)}
                  className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:col-start-1 sm:text-sm ${
                    actionLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequestsPage;